import { FiBookOpen, FiCreditCard, FiDollarSign, FiUsers } from 'react-icons/fi';
import { FaMoneyBillTrendUp } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Chart from 'react-google-charts';

import { BarChart, NumberIcon, SupportLatestWidget } from '../../components/widgets';
import {
	getAdminDashboardSummary,
	getAdminDashboardTimeseries,
} from '../../store/adminDashboard/adminDashboardActions';

function formatEuro(n) {
	const v = Number(n);
	if (!Number.isFinite(v)) return '0 €';
	return `${v.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

function formatInt(n) {
	const v = Number(n);
	if (!Number.isFinite(v)) return '0';
	return v.toLocaleString('fr-FR');
}

const pieOptions = {
	legend: { position: 'bottom' },
	pieSliceText: 'percentage',
	chartArea: { width: '90%', height: '75%' },
	colors: ['#191D51', '#E0BD48', '#DF2B47'],
};

const rolesBarOptions = {
	legend: { position: 'none' },
	colors: ['#191D51', '#E0BD48', '#DF2B47'],
	chartArea: { width: '85%', height: '70%' },
	hAxis: { title: '' },
	vAxis: { minValue: 0, format: '0' },
};

const trendsBarOptions = {
	legend: { position: 'top' },
	colors: ['#191D51', '#E0BD48'],
	chartArea: { width: '85%', height: '65%' },
	hAxis: { slantedText: true, slantedTextAngle: 45 },
	vAxis: { minValue: 0 },
};

const DashboardAdmin = () => {
	const dispatch = useDispatch();
	const { summary, loading, error, timeseries, timeseriesLoading, timeseriesError } = useSelector(
		(state) => state.adminDashboard,
	);

	useEffect(() => {
		dispatch(getAdminDashboardSummary());
		dispatch(getAdminDashboardTimeseries(6));
	}, [dispatch]);

	const appointmentPieData = useMemo(() => {
		const c = Number(summary.appointmentsCompleted) || 0;
		const p = Number(summary.appointmentsPending) || 0;
		const x = Number(summary.appointmentsCanceled) || 0;
		if (c + p + x === 0) {
			return [
				['Status', 'Count'],
				['No data', 1],
			];
		}
		return [
			['Status', 'Count'],
			['Completed', c],
			['Pending', p],
			['Canceled', x],
		];
	}, [summary]);

	const rolesColumnData = useMemo(() => {
		const t = Number(summary.tutorsCount) || 0;
		const s = Number(summary.studentsCount) || 0;
		const u = Number(summary.usersTotal) || 0;
		return [
			['Role', 'Count'],
			['Tutors', t],
			['Students', s],
			['All users', u],
		];
	}, [summary]);

	const trendsColumnData = useMemo(() => {
		const header = ['Month', 'Completed lessons', 'Platform fees (€)'];
		const rows = (timeseries || []).map((pt) => [
			pt.month,
			Number(pt.completedLessons) || 0,
			Number(pt.platformFees) || 0,
		]);
		return [header, ...rows];
	}, [timeseries]);

	return (
		<>
			<div className="general-informations mb-5 grid 2xl:grid-cols-5 sm:grid-cols-2 gap-4">
				<NumberIcon
					title="Users"
					number={loading ? '…' : formatInt(summary.usersTotal)}
					Icon={FiUsers}
				/>
				<NumberIcon
					title="Completed lessons"
					number={loading ? '…' : formatInt(summary.appointmentsCompleted)}
					Icon={FiBookOpen}
				/>
				<NumberIcon
					title="Lesson volume (GMV)"
					number={loading ? '…' : formatEuro(summary.completedLessonsGmv)}
					Icon={FiDollarSign}
				/>
				<NumberIcon
					title="Withdrawn"
					number={loading ? '…' : formatEuro(summary.totalWithdrawn)}
					Icon={FiCreditCard}
				/>
				<NumberIcon
					className="2xl:col-span-1 sm:col-span-2"
					title="Platform fees"
					number={loading ? '…' : formatEuro(summary.platformFeesTotal)}
					Icon={FaMoneyBillTrendUp}
				/>
			</div>
			{error && (
				<p className="mb-4 text-sm text-red-600" role="alert">
					{error}
				</p>
			)}
			<div className="grid lg:grid-cols-6 gap-4">
				<div className="col-span-1 lg:col-span-4 space-y-6 bg-white p-5 rounded-xl">
					<h2 className="text-nt-blue text-lg font-semibold">Analytics</h2>
					<div className="grid gap-6 md:grid-cols-2">
						<div>
							<h3 className="text-nt-blue mb-2 text-sm font-medium">Appointments</h3>
							<Chart
								chartType="PieChart"
								data={appointmentPieData}
								options={pieOptions}
								width="100%"
								height="280px"
							/>
						</div>
						<div>
							<h3 className="text-nt-blue mb-2 text-sm font-medium">Tutors / students / users</h3>
							<BarChart data={rolesColumnData} options={rolesBarOptions} />
						</div>
					</div>
					<div>
						<h3 className="text-nt-blue mb-2 text-sm font-medium">Last 6 months</h3>
						{timeseriesError && (
							<p className="mb-2 text-sm text-amber-700" role="status">
								{timeseriesError}
							</p>
						)}
						{timeseriesLoading ? (
							<p className="py-8 text-center text-sm text-slate-500">Loading trends…</p>
						) : (
							<BarChart data={trendsColumnData} options={trendsBarOptions} />
						)}
					</div>
					<div className="border-t border-slate-100 pt-4">
						<h3 className="text-nt-blue mb-3 text-sm font-medium">Overview</h3>
						<ul className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
							<li>
								<span className="text-slate-500">Admins (users)</span>{' '}
								<span className="font-medium">{loading ? '…' : formatInt(summary.usersAdmins)}</span>
							</li>
							<li>
								<span className="text-slate-500">Admin profiles</span>{' '}
								<span className="font-medium">{loading ? '…' : formatInt(summary.adminProfilesCount)}</span>
							</li>
							<li>
								<span className="text-slate-500">Tutors pending review</span>{' '}
								<span className="font-medium">{loading ? '…' : formatInt(summary.tutorsPendingModeration)}</span>
							</li>
						</ul>
						<div className="mt-4 flex flex-wrap gap-3">
							<Link
								className="rounded-lg bg-nt-blue px-4 py-2 text-sm font-medium text-white hover:opacity-90"
								to="/users/list"
							>
								User list
							</Link>
							<Link
								className="rounded-lg border border-nt-blue px-4 py-2 text-sm font-medium text-nt-blue hover:bg-slate-50"
								to="/admin/appointment"
							>
								All appointments
							</Link>
						</div>
					</div>
				</div>
				<div className="col-span-1 flex flex-col gap-4 lg:col-span-2">
					<div className="bg-white p-5 rounded-xl">
						<SupportLatestWidget />
					</div>
					<div className="bg-white p-5 rounded-xl">
						<h2 className="text-nt-blue max-h-[50px] mb-4">Actions</h2>
						<ul className="space-y-3 text-sm">
							<li className="flex justify-between gap-2">
								<Link className="text-nt-blue hover:underline font-medium" to="/users/moderation">
									Tutors pending review
								</Link>
								<span className="text-slate-600 tabular-nums">
									{loading ? '…' : formatInt(summary.tutorsPendingModeration)}
								</span>
							</li>
							<li className="flex justify-between gap-2">
								<Link className="text-nt-blue hover:underline font-medium" to="/withdraw">
									Pending withdrawals
								</Link>
								<span className="text-slate-600 tabular-nums">
									{loading ? '…' : formatInt(summary.pendingWithdrawalsCount)}
								</span>
							</li>
							<li className="text-slate-600">
								Pending withdrawal amount:{' '}
								<span className="font-medium text-slate-800">
									{loading ? '…' : formatEuro(summary.pendingWithdrawalsAmount)}
								</span>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</>
	);
};

export { DashboardAdmin };
